import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';

const CartContext = createContext(null);
const KEY = 'eg_cart';
const WISH_KEY = 'eg_wishlist';

const load = (k) => {
  try {
    return JSON.parse(localStorage.getItem(k)) || [];
  } catch (e) {
    return [];
  }
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => load(KEY));
  const [wishlist, setWishlist] = useState(() => load(WISH_KEY));
  const [open, setOpen] = useState(false);

  useEffect(() => localStorage.setItem(KEY, JSON.stringify(items)), [items]);
  useEffect(() => localStorage.setItem(WISH_KEY, JSON.stringify(wishlist)), [wishlist]);

  const lineKey = (productId, variantId) => `${productId}:${variantId || ''}`;

  // item: { productId, title, slug, image, price, compareAtPrice, variantId, variantTitle, quantity }
  const add = useCallback((item, openDrawer = true) => {
    setItems((prev) => {
      const key = lineKey(item.productId, item.variantId);
      const idx = prev.findIndex((i) => lineKey(i.productId, i.variantId) === key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + (item.quantity || 1) };
        return next;
      }
      return [...prev, { ...item, quantity: item.quantity || 1 }];
    });
    if (openDrawer) setOpen(true);
  }, []);

  const update = useCallback((productId, variantId, quantity) => {
    setItems((prev) =>
      prev
        .map((i) => (lineKey(i.productId, i.variantId) === lineKey(productId, variantId) ? { ...i, quantity } : i))
        .filter((i) => i.quantity > 0)
    );
  }, []);

  const remove = useCallback((productId, variantId) => {
    setItems((prev) => prev.filter((i) => lineKey(i.productId, i.variantId) !== lineKey(productId, variantId)));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const toggleWish = useCallback((product) => {
    setWishlist((prev) => {
      const exists = prev.find((p) => p._id === product._id);
      if (exists) return prev.filter((p) => p._id !== product._id);
      return [...prev, { _id: product._id, title: product.title, slug: product.slug, price: product.price, compareAtPrice: product.compareAtPrice, image: product.images?.[0]?.url || product.image || '' }];
    });
  }, []);

  const inWishlist = useCallback((id) => wishlist.some((p) => p._id === id), [wishlist]);

  const totals = useMemo(() => {
    const count = items.reduce((s, i) => s + i.quantity, 0);
    const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
    return { count, subtotal };
  }, [items]);

  return (
    <CartContext.Provider value={{ items, add, update, remove, clear, open, setOpen, wishlist, toggleWish, inWishlist, ...totals }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
